<template>
  <div
    :class="widgetClass"
    id="kd-ehdokasgalleria"
    ref="widget"
  >
    <Spinner
      v-if="loading"
    />
    <template v-if="($route.path.includes('/ehdokas/') && !loading)">
      <!-- Separate candidate view -->
      <router-view />
    </template>
    <template v-else>
      <!-- Candidate modal -->
      <CandidateDetails
        v-model="candidateModal"
        ref="candidateDetails"
        size="md"
        :item="candidateSelected"
        :communes="communesAll"
        :organization-types="organizationTypes"
        :countries="countries"
        :locale="config.locale"
        :pre-selected-tab="candidateSelectedTab"
        @close="onCandidateModalClose"
        @change-tab="onCandidateTabChange"
      />

      <!-- Widget embed modal-->
      <Modal
        v-model="embedModal"
        :title="$t('buttons.embed.label')"
        :close-on-bg-click="(false)"
        @close="embedModal = false"
        size="md"
      >
        <Embed
          :terms="terms"
          :config="config"
        />
      </Modal>

      <!-- Locale navigation -->
      <nav
        v-if="config.showLocale && locales.length"
        class="localeNav mb-double"
      >
        <h2 class="sr-only">
          {{ $t('titles.locale') }}
        </h2>
        <div class="localeNav__wrap">
          <button
            v-for="item in locales"
            :key="item.name"
            :class="{
              'localeNav__item': true,
              'localeNav__item--active' : config.locale == item.name
            }"
            @click="config.locale = item.name"
          >
            {{ item.title }}
          </button>
        </div>
      </nav>

      <!-- Search -->
      <div
        v-if="ready && config.showSearch && terms.election && Object.keys(terms.election).length"
        class="mb-double"
      >
        <h2 class="sr-only">
          {{ $t('titles.search') }}
        </h2>
        <div
          v-if="searchFormIsValid.valid === false && searchFormIsValid.reason.length"
          class="infobox p-single my-double"
        >
          <p>
            {{ $i18n.t(searchFormIsValid.reason) }}
          </p>
        </div>
        <InputfieldForm>
          <InputfieldFieldset>
            <InputfieldAutoComplete
              v-show="config.showElection && elections.length"
              v-model="terms.election"
              :label="$t('fields.election.label')"
              id="terms_election"
              :items="elections"
              :searchable="(false)"
              :required="(true)"
              :hide-asterisk="(true)"
              :multilang="(false)"
              :locale="config.locale"
            />
            <InputfieldAutoComplete
              v-show="config.showCommune && communesOfElection.length"
              v-model="terms.commune"
              :label="$t('fields.commune.label')"
              id="terms_commune"
              :items="communesOfElection"
              :required="(true)"
              :hide-asterisk="(true)"
              :multilang="(false)"
              :locale="config.locale"
            />
            <InputfieldAutoComplete
              v-show="config.showConstituency && constituenciesOfElection.length"
              v-model="terms.constituency"
              :label="terms.election.type == 'eduskunta' ? $t('fields.constituency.label') : $t('fields.welfareArea.label')"
              id="terms_constituency"
              :items="constituenciesOfElection"
              :required="(true)"
              :hide-asterisk="(true)"
              :multilang="(false)"
              :locale="config.locale"
            />
            <template v-if="searchFormIsValid.valid === true">
              <InputfieldAutoComplete
                v-show="config.showCandidateCommune && candidateCommunes.length"
                v-model="terms.candidateCommune"
                :label="$t('fields.commune.label')"
                id="terms_communes"
                :items="candidateCommunes"
                :required="(true)"
                :hide-asterisk="(true)"
                :multilang="(false)"
                :locale="config.locale"
              />
              <InputfieldAutoComplete
                v-show="config.showGender && genders.length"
                v-model="terms.gender"
                :label="$t('fields.gender.label')"
                id="terms_gender"
                :items="genders"
                :searchable="(false)"
                :required="(true)"
                :hide-asterisk="(true)"
                :multilang="(true)"
                :locale="config.locale"
              />
              <InputfieldRange
                v-show="config.showAge"
                v-model="terms.age"
                :label="$t('fields.age.label')"
                id="terms_age"
                :config="({
                  range: {
                    min: 18,
                    max: 100,
                  },
                  step: 1,
                  format: {
                    to: function(val) {
                      return parseInt(val)
                    },
                    from: function(val) {
                      return parseInt(val)
                    },
                  }
                })"
              />
            </template>
          </InputfieldFieldset>
        </InputfieldForm>
      </div>

      <!-- Candidate list -->
      <div v-show="searchFormIsValid.valid === true">
        <h2 class="sr-only">
          {{ $t('titles.candidates') }}
        </h2>
        <div
          :class="[
            'candidateList',
            loadingCandidates ? 'candidateList--loading' : null,
          ]"
        >
          <div
            v-if="config.showSummary"
            class="infobox p-single my-double"
          >
            <p>
              <template v-if="loadingCandidates">
                {{ $t('infoTexts.loadingCandidates') }}
              </template>
              <template v-else-if="!candidatesTotal">
                {{ $t('infoTexts.noCandidatesFound') }}
              </template>
              <template v-else>
                {{ $tc('infoTexts.candidatesFound', candidatesTotal) }}
              </template>
            </p>
          </div>
          <ul class="candidateList__list">
            <li
              v-for="item in candidatesVisible"
              :key="item.id"
              class="candidateList__item p-single"
            >
              <Card
                :id="('candidate-' + item.id)"
                :image="item.profilepic"
                :title="[
                  item.firstname,
                  item.lastname,
                  item.nonpartisan == 1 ? '(' + $t('candidate.independentAbbreviation') + ')' : null,
                ].join(' ')"
                :badge="item.number"
                :badge-label="$t('candidate.number')"
                @click="openCandidateModal(item)"
              >
                <p
                  v-if="item.title[config.locale]"
                  class="card__summary"
                >
                  {{ item.title[config.locale] }}
                </p>
                <!-- <p
                  v-if="item.location || item.age"
                  class="card__summary"
                >
                  {{ [
                    item.age ? item.age + ' ' + $t('candidate.yearsAbbreviation') : undefined,
                    item.location || undefined,
                  ].filter(item => !!item).join(', ') }}
                </p>
                <p
                  v-if="item.commune"
                  clas="card__summary"
                >
                  {{ item.commune.title }}
                </p> -->
                <p
                  v-if="item._summary"
                  class="card__summary"
                >
                  {{ item._summary }}
                </p>
                <p
                  v-if="config.enableDonation && item.bank && item.bank.length"
                  class="donationNotify"
                >
                  <font-awesome-icon
                    icon="heart"
                    class="donationNotify__icon"
                  />
                  <span class="donationNotify__text">
                    {{ $t('candidate.donationNotify') }}
                  </span>
                </p>
              </Card>
            </li>
          </ul>
        </div>
      </div>
      <div
        v-if="!config.autoPager && !loadingCandidates && nextPage"
        class="my-double"
      >
        <InputfieldButton
          class="loadMoreWrap"
          :text="$t('buttons.showMore.label')"
          @click="insertNextPage()"
        />
      </div>
      <div
        v-if="config.showEmbed"
        class="my-double"
      >
        <div class="-m-half text-center">
          <button
            class="
              m-half
              button
              button--text
            "
            @click="embedModal = true"
          >
            {{ $t('buttons.embed.label') }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>

export default {
  name: 'App',

  data: function () {
    return {
      ready: false, // Is app ready and watchers are allowed to run?
      loading: true, // Is app loading?
      loadingCandidates: true, // Are candidates being loaded?
      loadingMoreCandidates: false, // Is next page of candidates being loaded?
      widgetSizes: [], // Class list holding breakpoint classes based on widget size
      locales: [
        {
          name: 'fi',
          title: 'Suomeksi',
        },
        {
          name: 'en',
          title: 'In English',
        },
        {
          name: 'se',
          title: 'På svenska',
        },
      ],
      config: {
        locale: this.$i18n.fallbackLocale,
        showElection: process.env.NODE_ENV === 'development', // Show election?
        showLocale: process.env.NODE_ENV === 'development', // Show locale menu?
        showSearch: true, // Show search?
        showCommune: true, // Show commune selection?
        showCandidateCommune: true,
        showConstituency: true, // Show constitution selection?
        showGender: true, // Show gender selection?
        showAge: true, // Show age selection?
        showSummary: true, // Show search results summary text
        showEmbed: process.env.NODE_ENV === 'development', // Show embed link?
        candidateSelected: undefined, // Pre-selected candidate ID, which we will open into the modal
        candidateSelectedTab: undefined, // Pre-selected tab for candidate
        autoPager: false, // Use automatic pagination?
        enableDonation: true, // Hide donation label, also check CandidateDetails.vue
      },
      terms: { // Search terms
        election: {},
        constituency: {},
        commune: {},
        candidateCommune: {},
        gender: {},
        age: {
          start: 18,
          end: 100,
        },
        candidate_type: 'all',
      },
      pageSize: 18, // How many candidates per page?
      nextPage: null, // Next page number
      elections: [], // All elections
      communesAll: [], // All communes
      candidateCommunes: [],
      genders: [], // All genders
      countries: [], // All countries
      candidates: [], // Unfiltered candidates
      candidatesFiltered: [], // Filtered candidates
      candidatesVisible: [], // Candidates to be rendered
      organizationTypes: [], // All organization types
      candidatesTotal: null, // Total candidates found
      resizeTimeout: null, // Timeout used to throttle resize
      scrollTicking: false, // Is widget being scrolled?
      candidateModal: false, // Is candidate modal open?
      candidateSelected: undefined, // Currently selected candidate
      candidateSelectedTab: undefined, // Currently selected tab in candidate modal
      embedModal: false, // Is embed modal open?
    }
  },

  computed: {
    // Class object for widget
    widgetClass () {
      return [
        'widget',
        ...this.widgetSizes,
      ]
    },

    // We use this computed function to check that minimum requirements of
    // the search form are met, depending on election type
    searchFormIsValid () {
      let result = {
        valid: true,
        reason: null,
      }

      if (this.$route.path.includes('/ehdokas/')) {
        return result
      }

      try {
        if (!this.terms.election || !Object.keys(this.terms.election).length) throw this.$i18n.t('infoTexts.selectElection')

        if (this.terms.election.type === 'eduskunta') {
          if (!this.terms.constituency || !Object.keys(this.terms.constituency).length) throw this.$i18n.t('infoTexts.selectConstituency')
        } else if (this.terms.election.type === 'kunta') {
          if (!this.terms.commune || !Object.keys(this.terms.commune).length) throw this.$i18n.t('infoTexts.selectCommune')
        } else if (this.terms.election.type === 'alue') {
          if (!this.terms.constituency || !Object.keys(this.terms.constituency).length) throw this.$i18n.t('infoTexts.selectWelfareArea')
        } else if (this.terms.election.type === 'maakunta') {
          if (!this.terms.constituency || !Object.keys(this.terms.constituency).length) throw this.$i18n.t('infoTexts.selectWelfareArea')
        }
      } catch (reason) {
        result.valid = false
        result.reason = reason
      }

      return result
    },

    communesOfElection () {
      if (!this.terms.election || !Object.keys(this.terms.election).length) return []
      if (!this.terms.election.communes || !this.terms.election.communes.length) return []

      // Filter empty items
      let items = this.terms.election.communes.filter(item => item.id && item.title)

      return items.sort((a, b) => {
        return a.title.localeCompare(b.title, 'sv')
      })
    },

    constituenciesOfElection () {
      if (!this.terms.election || !Object.keys(this.terms.election).length) return []
      if (!this.terms.election.constituencies || !this.terms.election.constituencies.length) return []

      // Filter empty items
      let items = this.terms.election.constituencies.filter(item => item.id && item.title)

      return items.sort((a, b) => {
        return a.title.localeCompare(b.title, 'sv')
      })
    },
  },

  methods: {
    // Apply embed config based on user defined config object
    applyConfig (userConfig) {
      if (typeof userConfig != 'object') return

      // Set locale
      if (typeof userConfig.locale !== 'undefined') this.config.locale = userConfig.locale
      if (typeof userConfig.kieli !== 'undefined') this.config.locale = userConfig.kieli

      // General options
      if (typeof userConfig.showLocale !== 'undefined') this.config.showLocale = !!userConfig.showLocale
      if (typeof userConfig.showElection !== 'undefined') this.config.showElection = !!userConfig.showElection
      if (typeof userConfig.showSearch !== 'undefined') this.config.showSearch = !!userConfig.showSearch
      if (typeof userConfig.showCommune !== 'undefined') this.config.showCommune = !!userConfig.showCommune
      if (typeof userConfig.showConstituency !== 'undefined') this.config.showConstituency = !!userConfig.showConstituency
      if (typeof userConfig.showGender !== 'undefined') this.config.showGender = !!userConfig.showGender
      if (typeof userConfig.showAge !== 'undefined') this.config.showAge = !!userConfig.showAge
      if (typeof userConfig.showSummary !== 'undefined') this.config.showSummary = !!userConfig.showSummary
      if (typeof userConfig.autoPager !== 'undefined') this.config.autoPager = !!userConfig.autoPager
      if (typeof userConfig.showEmbed !== 'undefined') this.config.showEmbed = !!userConfig.showEmbed

      // Some of the parameters need to support different languages
      if (typeof userConfig.election !== 'undefined') this.config.election = userConfig.election
      if (typeof userConfig.vaali !== 'undefined') this.config.election = userConfig.vaali

      if (typeof userConfig.commune !== 'undefined') this.config.commune = userConfig.commune
      if (typeof userConfig.kunta !== 'undefined') this.config.commune = userConfig.kunta

      if (typeof userConfig.constituency !== 'undefined') this.config.constituency = userConfig.constituency
      if (typeof userConfig.alue !== 'undefined') this.config.constituency = userConfig.alue

      if (typeof userConfig.gender !== 'undefined') this.config.gender = userConfig.gender
      if (typeof userConfig.sukupuoli !== 'undefined') this.config.gender = userConfig.sukupuoli

      if (typeof userConfig.age_start !== 'undefined') this.terms.age.start = parseInt(userConfig.age_start)
      if (typeof userConfig.ika_min !== 'undefined') this.terms.age.start = parseInt(userConfig.ika_min)

      if (typeof userConfig.age_end !== 'undefined') this.terms.age.end = parseInt(userConfig.age_end)
      if (typeof userConfig.ika_max !== 'undefined') this.terms.age.end = parseInt(userConfig.ika_max)

      if (typeof userConfig.candidateSelected !== 'undefined') this.config.candidateSelected = userConfig.candidateSelected
      if (typeof userConfig.ehdokas !== 'undefined') this.config.candidateSelected = userConfig.ehdokas
      if (typeof userConfig.valilehti !== 'undefined') this.candidateSelectedTab = userConfig.valilehti

      if (typeof userConfig.candidate_type !== 'undefined' && ['green_women', 'green_youth', 'green_viite'].includes(userConfig.candidate_type)) {
        this.terms.candidate_type = userConfig.candidate_type
      }
    },

    // Update query string based on search terms
    updateQueryString (userParams = {}) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          ...userParams,
          kieli: this.config.locale,
          vaali: (this.terms.election && this.terms.election.id) ? this.terms.election.id : undefined,
          kunta: (/*this.config.showCommune &&*/ this.terms.commune && this.terms.commune.id) ? this.terms.commune.id : undefined,
          alue: (this.terms.constituency && this.terms.constituency.id) ? this.terms.constituency.id : undefined,
          ehdokaskunta: (this.config.showCandidateCommune && this.terms.candidateCommune && this.terms.candidateCommune.id) ? this.terms.candidateCommune.id : undefined,
          sukupuoli: (this.config.showGender && this.terms.gender && this.terms.gender.id) ? this.terms.gender.id : undefined,
          ika_min: (this.config.showAge && this.terms.age && this.terms.age.start && this.terms.age.start != 18) ? this.terms.age.start : undefined,
          ika_max: (this.config.showAge && this.terms.age && this.terms.age.end && this.terms.age.end != 100) ? this.terms.age.end : undefined,
          ehdokas: (this.candidateSelected && this.candidateSelected.name) ? this.candidateSelected.name : undefined,
          valilehti: (this.candidateSelected && this.candidateSelected.name && this.candidateSelectedTab === 'donate') ? this.candidateSelectedTab : undefined,
        },
      }).catch(() => {
        // Silently swallow errors, since Vue Router NavigationDuplicated error *will* occur
      })
    },

    // lauri: filter communes
    filterCandidateCommunes (communes = [], candidates = []) {
      let results = communes.filter((commune) => {
        return candidates.some((candidate) => {
          return candidate.commune.id === commune.id
        })
      })

      results.sort((a, b) => {
        return a.title.localeCompare(b.title, 'sv-SE')
      })

      results = [
        {
          id: null,
          title: this.$i18n.t('fields.all.label'),
        },
        ...results,
      ]

      return results
    },

    // Load candidates from API
    loadCandidates () {
      return new Promise((resolve, reject) => {
        if (this.searchFormIsValid.valid === false) return resolve([])

        // Set endpoint for candidates, depending on election type
        let endpoint = undefined

        if (this.terms.election.type === 'kunta' && this.terms.commune) {
          endpoint = this.terms.commune.url
        } else if (['eduskunta', 'maakunta', 'alue'].includes(this.terms.election.type) && this.terms.constituency) {
          endpoint = this.terms.constituency.url
        }

        if (typeof endpoint === 'undefined') return resolve([])

        // Cache busting (case LS-6475)
        endpoint += '?ts=' + (new Date()).setHours((new Date()).getHours() + 1, 0, 0, 0)

        this.loadingCandidates = true

        this.$axios.get(endpoint).then(res => {
          let items = res.data.items || []

          items = items.map(item => {
            // Parse summary text
            item._summary = [
              (!!parseInt(item.age_is_hidden) == false && item.age) ? item.age + ' ' + this.$i18n.t('candidate.yearsAbbreviation') : undefined,
              (this.terms.election && this.terms.election.type == 'kunta' && item.location) ? item.location : undefined,
              (this.terms.election && ['eduskunta', 'maakunta'].includes(this.terms.election.type) && item.commune) ? item.commune.title : undefined,
            ].filter(item => !!item).join(', ')

            return item
          })

          this.candidates = items

          return resolve(this.candidates)
        }).catch(function (error) {
          console.error(error)
          return reject(error)
        }).then(() => {
          this.loadingCandidates = false
        })
      })
    },

    // Filter candidates and split them into chunks
    filterCandidates (items = [], terms = {}) {
      if (!items.length) return []

      let results

      if (terms && Object.keys(terms).length) {
        results = items.filter(item => {
          // Filter by candidate type
          if (terms.candidate_type === 'green_women') {
            if (!item.green_women || item.green_women === false) return false
          } else if (terms.candidate_type === 'green_youth') {
            if (!item.green_youth || item.green_youth === false) return false
          } else if (terms.candidate_type === 'green_viite') {
            if (!item.green_viite || item.green_viite === false) return false
          }

          // Filter by candidate commune
          if (terms.candidateCommune && terms.candidateCommune.id) {
            if (!item.commune || item.commune.id != terms.candidateCommune.id) return false
          }

          // Filter by gender
          if (terms.gender && terms.gender.id) {
            if (!item.gender || item.gender.id != terms.gender.id) return false
          }

          // Filter by age
          if (terms.age && Object.keys(terms.age).length) {
            if (terms.age.start && item.age < terms.age.start) return false
            if (terms.age.end && item.age > terms.age.end) return false
          }

          return true
        })
      } else {
        results = items
      }

      // Set total of candidates
      this.candidatesTotal = results.length

      // Split into chunks
      return Array.from({ length: Math.ceil(results.length / this.pageSize) }, (v, i) =>
        results.slice(i * this.pageSize, i * this.pageSize + this.pageSize)
      )
    },

    insertNextPage () {
      const page = this.candidatesFiltered[this.nextPage]

      if (typeof page == 'undefined') return

      this.candidatesVisible = [
        ...this.candidatesVisible,
        ...page,
      ]

      if (typeof this.candidatesFiltered[this.nextPage +1] != 'undefined') {
        this.nextPage++
      } else {
        this.nextPage = null
      }
    },

    // Open candidate modal
    openCandidateModal (candidate) {
      this.candidateSelected = candidate
      this.candidateModal = true
      this.updateQueryString()
    },

    // When candidate modal is closed
    onCandidateModalClose () {
      this.candidateModal = false

      this.$nextTick(() => {
        // Focus back to the card, if it's visible
        const candidateCard = document.getElementById('candidate-' + this.candidateSelected.id)

        if (candidateCard) candidateCard.focus()

        this.candidateSelected = undefined
        this.updateQueryString()
      })
    },

    onCandidateTabChange (tab = null) {
      if (this.ready === false) return

      this.candidateSelectedTab = tab
      this.updateQueryString()
    },

    // When viewport is resized
    onResize () {
      clearTimeout(this.resizeTimeout)

      this.resizeTimeout = setTimeout(() => {
        // Find out widget size and build class array
        const widgetWidth = this.$refs.widget.offsetWidth
        let widgetSizes = []

        if (widgetWidth > 300) widgetSizes.push('widget--xs')
        if (widgetWidth > 400) widgetSizes.push('widget--sm')
        if (widgetWidth > 600) widgetSizes.push('widget--md')
        if (widgetWidth > 800) widgetSizes.push('widget--lg')
        if (widgetWidth > 1000) widgetSizes.push('widget--xl')
        if (widgetWidth > 1200) widgetSizes.push('widget--xxl')
        if (widgetWidth > 1400) widgetSizes.push('widget--xxxl')
        if (widgetWidth > 1600) widgetSizes.push('widget--xxxxl')
        if (widgetWidth > 1800) widgetSizes.push('widget--xxxxxl')

        this.widgetSizes = widgetSizes
      }, 200)
    },

    // When viewport is scrolled
    onScroll () {
      if (
        this.config.autoPager === false ||
        this.scrollTicking === true ||
        this.loadingCandidates === true ||
        this.loadingMoreCandidates === true ||
        !this.nextPage
      ) return

      this.scrollTicking = true

      window.requestAnimationFrame(() => {
        // If scroll position is near the bottom of the widget
        if ((window.innerHeight + window.scrollY) >= (this.$refs.widget.clientHeight * 0.8)) {
          this.insertNextPage()
        }

        this.scrollTicking = false
      })
    },
  },

  async mounted () {
    this.loading = true

    // Add event listener for resizing
    window.addEventListener('resize', this.onResize)

    // Initially run onResize to adjust layout
    let event

    if (typeof Event === 'function') {
      event = new Event('resize')
    } else {
      // We need this to support IE
      event = document.createEvent('Event')
      event.initEvent('resize', true, true)
    }

    window.dispatchEvent(event)

    // Load app config
    await this.$jsonApi.get('config.json').then(res => {
      this.elections = res.data.elections
      this.genders = [
        {
          id: null,
          title: {
            fi: 'Kaikki',
            se: 'Alla',
            en: 'All',
          },
        },
        ...res.data.sexes,
      ]
      this.candidateCommunes = [
        {
          id: null,
          title: this.$i18n.t('fields.all.label'),
        },
      ]
      this.communesAll = res.data.communes
      this.organizationTypes = res.data.organization_types
      this.countries = res.data.countries
    }).catch(function (error) {
      console.error(error)
    })

    // Apply embed config
    try {
      // Get config from embed config function
      if (typeof window.kd_ehdokasgalleria_config == 'function') {
        // Apply config
        this.applyConfig(window.kd_ehdokasgalleria_config())
      }

      // Get config from query string
      this.applyConfig(this.$router.currentRoute.query)

      // If we got query params in session storage
      if (sessionStorage.getItem('queryParams')) {
        // Apply config
        this.applyConfig(JSON.parse(sessionStorage.getItem('queryParams')))

        // Restore query
        this.updateQueryString(JSON.parse(sessionStorage.getItem('queryParams')))

        // Clear session storage
        sessionStorage.removeItem('queryParams')
      }
    } catch (error) {
      console.error(error)
    }

    // Set election
    const electionResult = this.elections.find(item => item.id == this.config.election)

    if (typeof electionResult == 'object') {
      this.terms.election = Object.assign({}, electionResult)
    } else {
      // Fallback to latest one
      this.terms.election = Object.assign({}, this.elections[this.elections.length - 1])
    }

    // Set candidate commune
    const candidateCommuneResult = this.candidateCommunes.find(item => item.id == this.config.candidateCommune)

    if (typeof candidateCommuneResult == 'object') {
      this.terms.candidateCommune = Object.assign(this.terms.candidateCommune, candidateCommuneResult)
    } else {
      // Fallback to first one
      this.terms.candidateCommune = Object.assign({}, this.candidateCommunes[0])
    }

    // Set gender
    const genderResult = this.genders.find(item => item.id == this.config.gender)

    if (typeof genderResult == 'object') {
      this.terms.gender = Object.assign(this.terms.gender, genderResult)
    } else {
      // Fallback to first one
      this.terms.gender = Object.assign({}, this.genders[0])
    }

    this.$nextTick(async () => {
      // If commune was pre-set, try to find it
      this.terms.commune = this.communesOfElection.find(item => item.id == this.config.commune) || null

      // If constituency was pre-set, try to find it
      this.terms.constituency = this.constituenciesOfElection.find(item => item.id == this.config.constituency) || null

      // Load candidates
      await this.loadCandidates()

      // Depending on pagination chunk size, we might already reached the bottom of the widget,
      // so we evaluate scroll just in case.
      this.onScroll()

      // If candidate was pre-selected, load details and open modal
      if (this.config.candidateSelected) {
        this.$nextTick(() => {
          // Search candidate by ID or name
          const candidate = this.candidates.find(item => {
            return item.id == this.config.candidateSelected || item.name == this.config.candidateSelected
          })

          if (candidate) {
            this.openCandidateModal(candidate)
          }
        })
      }

      // Update query string
      this.updateQueryString()

      // Add event listener for scrolling
      window.addEventListener('scroll', this.onScroll)

      // Mark app as ready to let watchers run
      this.ready = true
      this.loading = false
    })
  },

  beforeDestroy () {
    // Remove event listeners
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('scroll', this.onScroll)
  },

  watch: {
    'config.locale': {
      handler (val) {
        this.$root.$i18n.locale = val

        this.updateQueryString()
      },
    },

    'terms.election': {
      deep: true,
      handler () {
        if (!this.ready) return

        this.terms.constituency = {}
        this.terms.commune = {}

        this.loadCandidates()
      },
    },

    'terms.commune': {
      deep: true,
      handler () {
        if (!this.ready) return

        this.loadCandidates()
      },
    },

    'terms.constituency': {
      deep: true,
      handler () {
        if (!this.ready) return

        this.loadCandidates()
        this.terms.candidateCommune = this.filterCandidateCommunes(this.communesAll, this.candidates)
      },
    },

    terms: {
      deep: true,
      handler () {
        if (!this.ready) return

        this.updateQueryString()
        this.candidatesFiltered = this.filterCandidates(this.candidates, this.terms)
      },
    },

    candidateCommunes: {
      deep: true,
      handler () {
        // console.log('watch candidateCommunes changes')
      },
    },

    // When candidates are changed, filter them
    candidates: {
      deep: true,
      handler () {
        this.candidatesFiltered = this.filterCandidates(this.candidates, this.terms)
        this.candidateCommunes = this.filterCandidateCommunes(this.communesAll, this.candidates) // first page candidates
      },
    },

    // When filtered candidates are changed, clear all visible candidates and insert first page
    candidatesFiltered: {
      deep: true,
      handler () {
        this.candidatesVisible = []
        this.nextPage = 0
        this.insertNextPage()
      },
    },
  },
}
</script>

<style lang="scss">

#kd-ehdokasgalleria {
  min-height: 250px;
}

#kd-ehdokasgalleria,
#kd-ehdokasgalleria-modal-mount {
  @import 'assets/css/config.scss';
  @import 'assets/css/reset.scss';
  @import 'assets/css/multiselect.scss';
  @import 'assets/css/noui-slider.scss';
  @import 'assets/css/iconNav.scss';
  @import 'assets/css/form.scss';
  @import 'assets/css/spinner.scss';
  @import 'assets/css/modal.scss';
  @import 'assets/css/infobox.scss';
  @import 'assets/css/notify.scss';
  @import 'assets/css/badge.scss';
  @import 'assets/css/card.scss';
  @import 'assets/css/detailList.scss';
  @import 'assets/css/candidateList.scss';
  @import 'assets/css/candidateDetails.scss';
  @import 'assets/css/sharingButtons.scss';
  @import 'assets/css/localeNav.scss';

  position: relative;
  box-sizing: border-box;
  display: block;
  min-width: 300px;
  width: 100%;
  // font-family: $font-text;
  font-size: 16px;
  line-height: 1;
  background: transparent;
  // color: $color-gray--dark;
  color: $color-text--dark;

  * {
    box-sizing: border-box !important;
    font-family: inherit;
  }

  // Breakpoints
  &.widget--xs {
    .field--terms_commune,
    .field--terms_constituency,
    .field--terms_gender {
      flex: 1 1 50%;
    }
  }

  &.widget--sm {
    .field--sum,
    .field--sumCustom {
      flex: 1 1 50%;
    }

    .field--firstname,
    .field--lastname {
      flex: 1 1 50%;
    }
  }

  &.widget--md {
    .candidateList__item {
      width: 33.333%;
    }
  }

  &.widget--lg {
    .candidateList__item {
      width: 25%;
    }
  }

  &.widget--xl {
    .candidateList__item {
      width: 20%;
    }
  }

  &.widget--xxl {
    .candidateList__item {
      width: 16.666666%;
    }
  }

  &.widget--xxxxl {
    .candidateList__item {
      width: 14.285%;
    }
  }

  &.widget--xxxxxl {
    .candidateList__item {
      width: 12.5%;
    }
  }

  h1,
  h2,
  h3,
  h4 {
    color: inherit;
    font-weight: normal;
    line-height: 1.2;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  h1,
  h2 {
    margin: 1.5em 0 1em;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  h1 {
    font-size: 2em;
  }

  h2 {
    font-size: 1.2em;
    font-weight: 600;
  }

  h3 {
    font-size: 1em;
  }

  p,
  ul,
  ol,
  dl {
    font-size: .95em;
    line-height: 1.6;
    margin: 1em 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  ol {
    margin-left: 1.4em;
  }

  .rte {
    ul,
    ol {
      margin: 0 0 0 1.4em;
      padding: 0;
    }

    ul {
      list-style-type: disc;
    }

    ol {
       list-style-type: decimal;
    }
  }

  img {
    display: block;
    width: 100%;
    max-width: 100%;
    min-width: 100%;
    height: auto;
  }

  .loadMoreWrap {
    .field__content {
      text-align: center;
    }
  }

  .donationNotify {
    display: block;
    position: relative;
    color: $color-primary;
    margin-top: 2em;
    padding-left: 1.5em;
    line-height: 1.3;
    font-size: .8em;

    &__icon {
      position: absolute;
      left: 0;
    }

    &__text {
      font-weight: bold;
    }
  }

}
</style>
